import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Document } from '@getaccept/lib-shared/src/types/Document';
import { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';

export function cacheBuster(force: boolean) {
  let cachebuster: string;
  if (force) {
    cachebuster = `${Math.round(new Date().getTime() / 1000)}`;
  } else {
    cachebuster = 'init';
  }

  return cachebuster;
}

export const getBooleanForBackend = (value: boolean): string => (value ? 'true' : '');

export const sanitizeEmail = (email: string): string =>
  email.toLowerCase().replace('å', 'a').replace('ä', 'a').replace('ö', 'o');

export const jsonToParams = (json: object, separator = '&') =>
  Object.keys(json)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`)
    .join(separator);

export const uniqueArray = (value: string[]): string[] => {
  const set = new Set(value);
  return Array.from(set);
};

export function isWindows(nav: Navigator): boolean {
  return nav.userAgent.indexOf('Windows') != -1;
}

export function getDocumentStatusClass(documentStatus: string) {
  switch (documentStatus) {
    case 'draft':
      return 'ga-default';

    case 'hardbounced':
    case 'partlyrejected':
    case 'rejected':
    case 'recalled':
    case 'lost':
    case 'expired':
      return 'ga-danger';

    case 'deferred':
    case 'reviewed':
    case 'viewed':
    case 'commented':
    case 'clicked':
    case 'downloaded':
    case 'attachment':
    case 'attachmentopened':
    case 'waitingforsignature':
    case 'partlysigned':
      return 'ga-warning';

    case 'sent':
    case 'sms':
    case 'reminder':
    case 'video':
    case 'paused':
    case 'updated':
      return 'ga-primary';

    case 'processing':
    case 'importing':
    case 'printed':
    case 'created':
    case 'sealed':
    case 'scheduled':
    case 'waiting':
    case 'archived':
      return 'ga-info';

    case 'delivered':
    case 'approved':
    case 'verified':
    case 'signed':
      return 'ga-success';

    case 'signedwithoutverification':
      return 'ga-success unverified';

    default:
      return 'ga-default';
  }
}

export function inProduction(currentWindow: Window): boolean {
  return currentWindow.location.href.includes('getaccept.com');
}

export function getBinaryPayloadValue(isSet: boolean): string {
  return isSet ? '1' : '0';
}

export function getDocumentStatusTranslation(status: DocumentStatus): string {
  if (status === DocumentStatus.Recalled) {
    return t('Deleted');
  }
  return t(`document-status-${status}`);
}

export function getTemplateIdFromRegistrationCampaign(registrationCampaign: string): string {
  const pattern = 'templateid_';
  const patternIndex = registrationCampaign.indexOf(pattern) + pattern.length;
  return registrationCampaign.substring(
    patternIndex,
    registrationCampaign.indexOf('|', patternIndex) === -1
      ? registrationCampaign.length
      : registrationCampaign.indexOf('|', patternIndex)
  );
}

export function hasValidString(value: string): boolean {
  return !!value && value !== 'undefined' && value !== 'null' && value !== '';
}

export function getCompanyName(document: Document): string {
  if (!document?.companyName && !document?.recipients?.length) {
    return '';
  }

  const companyNames = document.recipients
    .filter(recipient => !!recipient.companyName)
    .map(recipient => recipient.companyName);

  if (document.companyName) {
    companyNames.unshift(document.companyName);
  }
  return [...new Set(companyNames)].join(', ');
}
